/**
 * Thème clair/sombre.
 *
 * Le thème suit `prefers-color-scheme` par défaut (voir `styles.css`) ; ce module ajoute
 * un choix explicite, mémorisé, qui prend le pas sur la préférence système — exactement
 * le même schéma que la langue (`i18n/index.ts`).
 */

import { create } from 'zustand';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'attrape:theme';

/** Ce module doit rester chargeable hors navigateur (tests unitaires). */
function hasBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Préférence enregistrée, puis préférence système, puis clair. */
export function detectTheme(): Theme {
  if (hasBrowserStorage()) {
    try {
      const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (isTheme(stored)) return stored;
    } catch {
      // Stockage refusé (navigation privée, politique) : on poursuit la détection.
    }
  }
  return systemPrefersDark() ? 'dark' : 'light';
}

/** Pose l'attribut lu par `styles.css` (`:root[data-theme]`) — la seule chose qui compte
    pour l'affichage, le reste n'est que mémorisation de la préférence. */
function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: detectTheme(),
  setTheme: (theme) => {
    if (hasBrowserStorage()) {
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, theme);
      } catch {
        // Préférence non conservée : sans conséquence, la détection reprendra la main.
      }
    }
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

// Posé dès le chargement du module, avant le premier rendu : sans ça, la page s'affiche
// un instant dans le thème par défaut du navigateur avant de basculer sur le choix mémorisé.
applyTheme(useThemeStore.getState().theme);
